import { useState } from 'react'
import {
  fetcher,
  AdminTriviaTemplatesEndpoints,
  AdminTriviaAssignmentsEndpoints,
  IUseTriviaTemplates,
  IUseTriviaAssignments,
  IUseTriviaCalendar,
  ICreateTriviaTemplate,
  ICreateTriviaAssignment,
  ICreateBulkTriviaAssignments
} from '@olagg/api-hooks'
import {
  TriviaQuestionTemplate,
  TriviaDailyAssignment,
  TriviaTemplatesResponse,
  TriviaCalendarResponse
} from '@olagg/db-types'

/**
 * Hook para manejar Trivia v2
 *
 * La API v2 separa:
 * - Templates: Catálogo de preguntas reutilizables
 * - Assignments: Asignaciones de templates a fechas específicas
 */
const useTrivia = () => {
  const [loading, setLoading] = useState(false)

  // =====================
  // Templates (Catálogo)
  // =====================

  const getTriviaTemplates = async (filter: IUseTriviaTemplates = {}): Promise<TriviaTemplatesResponse> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaTemplatesEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error fetching trivia templates:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getTriviaTemplate = async (id: string): Promise<TriviaQuestionTemplate> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaTemplatesEndpoints.find(id)
      const response = await fetcher(endpoint)
      return response.triviaQuestionTemplate
    } catch (error) {
      console.error('Error fetching trivia template:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createTriviaTemplate = async (data: { template: ICreateTriviaTemplate }): Promise<TriviaQuestionTemplate> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaTemplatesEndpoints.create(data)
      const response = await fetcher(endpoint)
      return response.triviaQuestionTemplate
    } catch (error) {
      console.error('Error creating trivia template:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateTriviaTemplate = async (id: string, data: any): Promise<TriviaQuestionTemplate> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaTemplatesEndpoints.update(id, data)
      const response = await fetcher(endpoint)
      return response.triviaQuestionTemplate
    } catch (error) {
      console.error('Error updating trivia template:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteTriviaTemplate = async (id: string): Promise<void> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaTemplatesEndpoints.delete(id)
      await fetcher(endpoint)
    } catch (error) {
      console.error('Error deleting trivia template:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const bulkCreateTriviaTemplates = async (file: File): Promise<any> => {
    try {
      setLoading(true)
      const formData = new FormData()
      formData.append('file', file)
      const endpoint = AdminTriviaTemplatesEndpoints.bulkCreate(formData)
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error bulk creating trivia templates:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  // =====================
  // Assignments (Calendario)
  // =====================

  const getTriviaAssignments = async (filter: IUseTriviaAssignments = {}): Promise<TriviaDailyAssignment[]> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaAssignmentsEndpoints.all(filter)
      const response = await fetcher(endpoint)
      return response.assignments
    } catch (error) {
      console.error('Error fetching trivia assignments:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getTriviaCalendar = async (filter: IUseTriviaCalendar): Promise<TriviaCalendarResponse> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaAssignmentsEndpoints.calendar(filter)
      const response = await fetcher(endpoint)
      return response
    } catch (error) {
      console.error('Error fetching trivia calendar:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createTriviaAssignment = async (data: { assignment: ICreateTriviaAssignment }): Promise<TriviaDailyAssignment> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaAssignmentsEndpoints.create(data)
      const response = await fetcher(endpoint)
      return response.assignment
    } catch (error) {
      console.error('Error creating trivia assignment:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const bulkCreateTriviaAssignments = async (data: ICreateBulkTriviaAssignments): Promise<TriviaDailyAssignment[]> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaAssignmentsEndpoints.bulkCreate(data)
      const response = await fetcher(endpoint)
      return response.assignments
    } catch (error) {
      console.error('Error bulk creating trivia assignments:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteTriviaAssignment = async (id: string): Promise<void> => {
    try {
      setLoading(true)
      const endpoint = AdminTriviaAssignmentsEndpoints.delete(id)
      await fetcher(endpoint)
    } catch (error) {
      console.error('Error deleting trivia assignment:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    // Templates (Catálogo)
    getTriviaTemplates,
    getTriviaTemplate,
    createTriviaTemplate,
    updateTriviaTemplate,
    deleteTriviaTemplate,
    bulkCreateTriviaTemplates,
    // Assignments (Calendario)
    getTriviaAssignments,
    getTriviaCalendar,
    createTriviaAssignment,
    bulkCreateTriviaAssignments,
    deleteTriviaAssignment
  }
}

export default useTrivia
